import { useState } from 'react';
import { Input, StatusPill } from '../../../design-system';
import '../../styles/TallyPanel.css';

// Where this machine keeps its Tally companies.
//
// ⚠️ DISCOVERED BY THE AGENT, NOT TYPED BY ANYBODY, and that distinction is the
// whole point of this component. A folder like `Y:\OfficeTally` means something
// on exactly one practice's PC — any design that needs it entered by hand is a
// one-time setup step per machine, which is right on the computer it was set up
// on, silently wrong on the next one, and stale the moment somebody moves their
// data. Tally already knows and writes it down in `tally.ini`; the agent reads
// that on its self-check beat and reports it. See agent/src/tallyConfig.ts.
//
// ⚠️ THE OVERRIDE IS AN ESCAPE HATCH, NOT THE MECHANISM. It exists for a Tally
// installed somewhere the agent did not look, or a firm deliberately pointing us
// elsewhere — and it is hidden until asked for, so nobody reads it as a step
// they are expected to complete.

function TallyDataPath({ agent, override, onOverride }) {
  const [showOverride, setShowOverride] = useState(!!override);

  if (!agent) return null;

  const detected = agent.tallyDataPathDetected || '';
  const effective = agent.tallyDataPathEffective || '';
  const folders = agent.tallyCompanyFolders || [];
  // An older build reports none of this. Saying so beats rendering "not found",
  // which reads as a broken Tally rather than an agent that predates the check.
  const canReport = !!agent.tallyLayoutSeenAt || !!detected || folders.length > 0;

  return (
    <div className="tally-datapath">
      <div className="tally-status-row">
        <strong>Tally data folder:</strong>
        {effective ? (
          <span className="tally-mono">{effective}</span>
        ) : (
          <StatusPill variant="warning" label="Not found yet" />
        )}
        {override && effective === override && <StatusPill variant="info" label="set by hand" />}
      </div>

      {detected ? (
        <p className="tally-muted">
          Found automatically{agent.tallyIniPath ? ` in ${agent.tallyIniPath}` : ''}.
          {folders.length > 0 && (
            <>
              {' '}
              {/* ⚠️ THE NUMBER THAT MATTERS ON A MACHINE LIKE THIS ONE. Tally
                  lists one directory, not a tree, so a firm filing each client in
                  its own folder has a data path per client. The agent counts
                  them because only a process on that PC can see the disk. */}
              <strong>{folders.length}</strong> folder{folders.length === 1 ? '' : 's'} on
              that computer hold Tally companies.
            </>
          )}
        </p>
      ) : canReport ? (
        <p className="tally-warn">
          The agent could not find Tally&apos;s data folder on that computer — it looks for
          <code> tally.ini</code> beside the running Tally and in the usual install folders.
          Set it by hand below.
        </p>
      ) : (
        <p className="tally-muted">
          This computer&apos;s agent is too old to report where Tally keeps its data.
          Update it, or set the folder by hand below.
        </p>
      )}

      {!showOverride ? (
        <button
          type="button"
          className="tally-linkish tally-linkish-inline"
          onClick={() => setShowOverride(true)}
        >
          Use a different folder
        </button>
      ) : (
        <Input
          label="Use this folder instead"
          value={override}
          placeholder={detected || 'Y:\\OfficeTally'}
          onChange={(e) => onOverride(e.target.value)}
          helperText={
            'Only needed when the folder above is wrong or missing. The same value '
            + 'Tally shows under Select Company \u2192 Specify Path. Saved against this '
            + 'computer when you scan; clear it to go back to what the agent finds.'
          }
        />
      )}
    </div>
  );
}

export default TallyDataPath;
